import { useState, useEffect, useRef } from "react";
import { useModalStore } from "../../../stores/modalStore.js";

export function EmojiManagerModal() {
  const { isOpen, type, data, onClose } = useModalStore();
  const serverId = data?.serverId;

  const [emoji,      setEmoji]      = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [uploading,  setUploading]  = useState(false);
  const [nameInput,  setNameInput]  = useState('');
  const [preview,    setPreview]    = useState(null);
  const [file,       setFile]       = useState(null);
  const [error,      setError]      = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (!isOpen || type !== 'emojiManager' || !serverId) return;
    loadEmoji();
  }, [isOpen, type, serverId]);

  if (!isOpen || type !== 'emojiManager') return null;

  const loadEmoji = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/servers/${serverId}/emoji`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      });
      const d = await res.json();
      setEmoji(d.emoji || []);
    } catch (_) {}
    setLoading(false);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 256 * 1024) { setError('File must be under 256 KB'); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    if (!nameInput) setNameInput(f.name.replace(/\.[^.]+$/, '').replace(/[^a-z0-9_]/gi, '_').toLowerCase().slice(0, 32));
    setError('');
  };

  const handleUpload = async () => {
    if (!file || !nameInput.trim()) { setError('Name and image are required'); return; }
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', nameInput.trim().toLowerCase());
      const res = await fetch(`/api/servers/${serverId}/emoji`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        body: fd,
      });
      const d = await res.json();
      if (d.success) {
        setEmoji(prev => [...prev, d.emoji]);
        setFile(null); setPreview(null); setNameInput(''); fileRef.current && (fileRef.current.value = '');
      } else setError(d.error || 'Upload failed');
    } catch { setError('Upload failed'); }
    setUploading(false);
  };

  const handleDelete = async (emojiId) => {
    if (!confirm('Delete this emoji?')) return;
    try {
      await fetch(`/api/servers/${serverId}/emoji/${emojiId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      });
      setEmoji(prev => prev.filter(e => e.id !== emojiId));
    } catch (_) {}
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div style={{ width: '100%', maxWidth: 560, maxHeight: '88vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary,#13141f)', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,0.6)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>😄 Custom Emoji</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{emoji.length}/50 emoji slots used</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>

          {/* Upload section */}
          {emoji.length < 50 && (
            <section>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>Upload Emoji</div>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                {/* Preview / drop zone */}
                <div
                  onClick={() => fileRef.current?.click()}
                  style={{ width: 80, height: 80, borderRadius: 12, background: 'rgba(255,255,255,0.04)', border: '2px dashed rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, overflow: 'hidden', transition: 'border-color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(99,102,241,0.5)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'}
                >
                  {preview
                    ? <img src={preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    : <span style={{ fontSize: 28, opacity: 0.3 }}>＋</span>}
                </div>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" style={{ display: 'none' }} onChange={handleFileChange} />

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input value={nameInput} onChange={e => setNameInput(e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase())} placeholder="emoji_name"
                    style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, outline: 'none', fontFamily: 'monospace' }} />
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>Allowed: letters, numbers, underscores. Max 256 KB.</div>
                  {error && <div style={{ fontSize: 12, color: '#fca5a5' }}>{error}</div>}
                  <button onClick={handleUpload} disabled={uploading || !file}
                    style={{ padding: '8px 16px', borderRadius: 8, background: file ? 'var(--color-primary,#6366f1)' : 'rgba(255,255,255,0.07)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: file ? 'pointer' : 'not-allowed', opacity: uploading ? 0.6 : 1, alignSelf: 'flex-start' }}>
                    {uploading ? 'Uploading…' : 'Upload Emoji'}
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* Emoji list */}
          <section>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
              Server Emoji {loading ? '…' : `(${emoji.length})`}
            </div>
            {loading ? (
              <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>Loading…</div>
            ) : emoji.length === 0 ? (
              <div style={{ padding: '24px 0', textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: 13 }}>No custom emoji yet. Upload one above!</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {emoji.map(e => (
                  <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8 }}>
                    <img src={e.url} alt={e.name} style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 4 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', fontFamily: 'monospace' }}>:{e.name}:</div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>Type :{e.name}: to use</div>
                    </div>
                    <button onClick={() => handleDelete(e.id)}
                      style={{ padding: '4px 10px', borderRadius: 6, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)', color: '#fca5a5', fontSize: 12, cursor: 'pointer' }}>
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
